/**
 * Agrupa DocumentReference em transações (receita e/ou ANVISA).
 *
 * Só entra documento do modelo atual (`transaction-id`).
 * Sem transaction-id = histórico.
 * Um dos dois (receita ou ANVISA) já gera a transação; o par incompleto
 * fica em pendentes via resolveTransactionOpState.
 *
 * @module documentTransactions
 */

import type { DocumentReference, Patient } from "fhir/r5";
import dayjs from "dayjs";
import {
  extractDocumentTransactionId,
  isCancelledDocumentStatus,
  isLegacySupportingDocument,
  isRejectedDocumentStatus,
} from "src-next/modules/patient-supporting-documents";

import type { PatientEcommerceOpState } from "./patientEcommerceOpState";

export const SHARED_DOCUMENT_CATEGORY_CODES = [
  "personal_document",
  "proof_of_residence",
] as const;

export const TRANSACTION_DOCUMENT_CATEGORY_CODES = [
  "medical_prescription",
  "anvisa_import_authorization",
] as const;

export type TransactionOpState = PatientEcommerceOpState | "purchase_completed";

export interface TransactionSharedDocs {
  personal?: DocumentReference;
  residence?: DocumentReference;
}

export interface DocumentTransaction {
  transactionId: string;
  prescriptionDoc?: DocumentReference;
  anvisaDoc?: DocumentReference;
  sharedDocs: TransactionSharedDocs;
}

export interface DocumentTransactionRow {
  transactionId: string;
  patient: Patient;
  prescriptionDoc?: DocumentReference;
  anvisaDoc?: DocumentReference;
  sharedDocs: TransactionSharedDocs;
  opState: TransactionOpState;
  purchaseCompleted?: boolean;
  createdAt?: string;
}

export function categoryCodeOfDocument(
  doc: DocumentReference,
): string | undefined {
  return (
    doc.category?.[0]?.coding?.[0]?.code ||
    doc.category?.[0]?.text ||
    undefined
  );
}

export function isHistoricalDocument(doc: DocumentReference): boolean {
  return (
    isCancelledDocumentStatus(doc.docStatus) ||
    isRejectedDocumentStatus(doc.docStatus) ||
    isLegacySupportingDocument(doc)
  );
}

function dateValue(iso?: string): number {
  if (!iso) return 0;
  const parsed = dayjs(iso);
  return parsed.isValid() ? parsed.valueOf() : 0;
}

export function pickLatestDocument(
  docs: DocumentReference[],
): DocumentReference | undefined {
  if (docs.length <= 1) return docs[0];
  return [...docs].sort(
    (left, right) => dateValue(right.date) - dateValue(left.date),
  )[0];
}

function sortByDateDesc(docs: DocumentReference[]): DocumentReference[] {
  return [...docs].sort(
    (left, right) => dateValue(right.date) - dateValue(left.date),
  );
}

export function transactionCreatedAt(
  transaction: DocumentTransaction,
): string | undefined {
  const dates = [
    transaction.prescriptionDoc?.date,
    transaction.anvisaDoc?.date,
  ].filter((value): value is string => Boolean(value));
  if (!dates.length) return undefined;
  return dates.sort((left, right) => dateValue(right) - dateValue(left))[0];
}

export function formatTransactionLabel(
  transaction: Pick<DocumentTransaction, "transactionId"> & {
    createdAt?: string;
  },
): string {
  const dateLabel = transaction.createdAt
    ? dayjs(transaction.createdAt).format("DD/MM/YYYY")
    : "";
  if (dateLabel) return dateLabel;
  if (!transaction.transactionId || transaction.transactionId.startsWith("pending:")) {
    return "Nova transação";
  }
  return `#${transaction.transactionId.slice(0, 8)}`;
}

/**
 * Agrupa docs atuais em transações. Pessoal/residência entram em todas.
 * Gera transação assim que receita ou ANVISA tiverem o mesmo transaction-id.
 */
export function groupDocumentsIntoTransactions(
  docs: DocumentReference[],
): DocumentTransaction[] {
  const active = docs.filter((doc) => !isHistoricalDocument(doc));

  const byCategory = new Map<string, DocumentReference[]>();
  for (const doc of active) {
    const code = categoryCodeOfDocument(doc);
    if (!code) continue;
    const list = byCategory.get(code) ?? [];
    list.push(doc);
    byCategory.set(code, list);
  }

  const sharedDocs: TransactionSharedDocs = {
    personal: pickLatestDocument(byCategory.get("personal_document") ?? []),
    residence: pickLatestDocument(byCategory.get("proof_of_residence") ?? []),
  };

  const prescriptions = sortByDateDesc(
    byCategory.get("medical_prescription") ?? [],
  );
  const anvisas = sortByDateDesc(
    byCategory.get("anvisa_import_authorization") ?? [],
  );

  const byTxId = new Map<
    string,
    { prescription?: DocumentReference; anvisa?: DocumentReference }
  >();

  for (const doc of prescriptions) {
    const txId = extractDocumentTransactionId(doc);
    if (!txId) continue;
    const current = byTxId.get(txId) ?? {};
    if (!current.prescription) current.prescription = doc;
    byTxId.set(txId, current);
  }

  for (const doc of anvisas) {
    const txId = extractDocumentTransactionId(doc);
    if (!txId) continue;
    const current = byTxId.get(txId) ?? {};
    if (!current.anvisa) current.anvisa = doc;
    byTxId.set(txId, current);
  }

  const transactions: DocumentTransaction[] = [];
  for (const [transactionId, pair] of byTxId) {
    if (!pair.prescription && !pair.anvisa) continue;
    transactions.push({
      transactionId,
      prescriptionDoc: pair.prescription,
      anvisaDoc: pair.anvisa,
      sharedDocs,
    });
  }

  return transactions.sort((left, right) => {
    const leftDate = dateValue(transactionCreatedAt(left));
    const rightDate = dateValue(transactionCreatedAt(right));
    return rightDate - leftDate;
  });
}

export function collectTransactionDocuments(
  transaction: DocumentTransaction,
): DocumentReference[] {
  return [
    transaction.prescriptionDoc,
    transaction.anvisaDoc,
    transaction.sharedDocs.personal,
    transaction.sharedDocs.residence,
  ].filter((doc): doc is DocumentReference => Boolean(doc));
}

export function findTransactionById(
  transactions: DocumentTransaction[],
  transactionId: string | undefined,
): DocumentTransaction | undefined {
  if (!transactionId) return transactions[0];
  return (
    transactions.find((item) => item.transactionId === transactionId) ??
    transactions[0]
  );
}

export function resolveSharedDocInheritance(
  shared: DocumentReference | undefined,
  lastApprovedId: string | undefined,
): boolean {
  if (!shared?.id || !lastApprovedId) return false;
  return shared.id === lastApprovedId;
}

export function lastApprovedSharedDocId(
  docs: DocumentReference[] | undefined,
): string | undefined {
  if (!docs?.length) return undefined;
  return pickLatestDocument(docs)?.id;
}

export { extractDocumentTransactionId };
